import { useCallback, useState, type CSSProperties } from 'react';
import { useStableCallback } from '../use_stable_callback.js';

/**
 * A prévia ANCORADA NO PÉ do mapa do palco estreito (#3108): o card que o
 * toque no pino abre encostado na folha. A casca é de cada produto (o card do
 * hotel, a folha do carro selecionado); a medida e o vão são estes.
 */

/** O vão entre o card da prévia ancorada no pé e a folha abaixo dele. */
export const DOCKED_PREVIEW_GAP_PX = 12;

/** Mede o card ancorado no pé e avisa a cada mudança de altura (a faixa de
 *  fotos e o nome do hotel variam); ao desmontar avisa `undefined`. Sem
 *  `ResizeObserver` (jsdom) fica na medida do primeiro layout. */
export function useDockedHeight(onResize: ((height: number | undefined) => void) | undefined) {
  const notify = useStableCallback((height: number | undefined) => {
    onResize?.(height);
  });
  return useCallback(
    (element: HTMLElement | null) => {
      if (!element) {
        notify(undefined);
        return undefined;
      }
      const report = () => {
        notify(element.getBoundingClientRect().height);
      };
      report();
      if (typeof ResizeObserver === 'undefined') return undefined;
      const observer = new ResizeObserver(report);
      observer.observe(element);
      // React 19 runs this cleanup instead of calling the ref with `null`, so
      // the detach has to report `undefined` here too.
      return () => {
        observer.disconnect();
        notify(undefined);
      };
    },
    [notify],
  );
}

/** O bloco que o card ancorado no pé toma do mapa visível (altura + vão), em
 *  `--hui-map-preview-block` para a `<section>` do mapa: o palco centra os
 *  controles de vista no mapa que sobra (`--hui-map-visible`), e sem descontar
 *  o card eles ficavam embaixo dele no celular (#3845). 0 sem card. */
export function useDockedPreviewBlock(): {
  readonly style: CSSProperties;
  readonly onDockedResize: (height: number | undefined) => void;
} {
  const [height, setHeight] = useState<number>();
  const block = height === undefined ? 0 : height + DOCKED_PREVIEW_GAP_PX;
  return {
    style: { '--hui-map-preview-block': `${String(block)}px` } as CSSProperties,
    onDockedResize: setHeight,
  };
}
